/**
 * Static data for the full logged-in dashboard preview shown on the landing
 * (design screen 1b · LOGGED-IN HOME). The corporate home preview (2a) was
 * dropped with corporate's move to "coming soon" (Ajay, 2026-11-01).
 *
 * MARKETING-STATIC: every name, figure and date here is fabricated demo
 * content, permitted on the logged-out landing only — never inside the
 * logged-in app (grounded-AI principle). The SHAPE mirrors the live criminal
 * dashboard (2026-11-03): rail with Start Here first, greeting + docket line,
 * the Cause List week strip, the Urgent Deadlines panel (liberty clocks with
 * the ledger's own headline wording, then filing deadlines), and the Court
 * updates strip. Liberty copy keeps every hedge the real ledger carries
 * ("may accrue", a question) — a marketing mock must not state more than the
 * product would.
 */

export interface NavItem {
  icon?: string;
  label: string;
  active?: boolean;
  heading?: boolean;
  soon?: boolean;
}

export interface RailUser {
  initial: string;
  name: string;
  plan: string;
  practice: string;
}

export interface WeekDay {
  /** "TODAY" or the short weekday. */
  day: string;
  date: number;
  /** Hearings that day; 0 renders the "·" placeholder like the real strip. */
  count: number;
  selected?: boolean;
  today?: boolean;
}

export interface CauseRow {
  title: string;
  /** Case number · time, exactly as the live sub-line joins them. */
  sub: string;
  chip: string;
  court: string;
}

export type LibertyRow =
  | {
      kind: 'exposed';
      days: string;
      headline: string;
      caseLine: string;
    }
  | {
      kind: 'needs_answer';
      headline: string;
      caseLine: string;
    };

export interface DeadlineRow {
  days: string;
  what: string;
  matter: string;
  tag: string;
}

export interface CourtUpdateRow {
  /** Bold relative-day prefix the strip adds for a fresh listing. */
  lead?: string;
  summary: string;
  meta: string;
}

/* ── 1b · Criminal logged-in home ─────────────────────────────── */

export const CRIMINAL_NAV: NavItem[] = [
  { heading: true, label: 'MY WORK' },
  { icon: '◎', label: 'Start Here' },
  { icon: '▦', label: 'Dashboard', active: true },
  { icon: '⇥', label: 'Bring your cases' },
  { icon: '▭', label: 'Calendar' },
  { icon: '◔', label: 'Clients' },
  { icon: '▣', label: 'Matters' },
  { icon: '≡', label: 'Cause List' },
  { icon: '✎', label: 'My Diary' },
  { icon: '▤', label: 'Brief Bank' },
  { heading: true, label: 'CASE ANALYSIS' },
  { icon: '▣', label: 'Analyze FIR' },
  { icon: '◈', label: 'Build Case Theory' },
  { icon: '⚖', label: 'Bail Grounds Checklist' },
  { icon: '⚙', label: 'Settings' },
];

export const CRIMINAL_RAIL_USER: RailUser = {
  initial: 'A',
  name: 'Alok Mohanty',
  plan: 'MAX',
  practice: 'CRIMINAL PRACTICE',
};

export const CRIMINAL_DOCKET_LINE = {
  hearings: '2',
  nextHearing: 'Thu 5 Nov',
  urgent: '1',
  theories: '3',
  date: 'Tuesday, 3 November 2026',
};

export const CRIMINAL_WEEK_LABEL = '3 NOV – 9 NOV';

export const CRIMINAL_WEEK: WeekDay[] = [
  { day: 'TODAY', date: 3, count: 2, selected: true, today: true },
  { day: 'WED', date: 4, count: 0 },
  { day: 'THU', date: 5, count: 1 },
  { day: 'FRI', date: 6, count: 0 },
  { day: 'SAT', date: 7, count: 0 },
  { day: 'SUN', date: 8, count: 0 },
  { day: 'MON', date: 9, count: 0 },
];

export const CRIMINAL_CAUSE_LIST: CauseRow[] = [
  {
    title: 'State vs. Rohit Kumar',
    sub: 'CRL.M.C 4421/2026 · 10:30',
    chip: 'BAIL',
    court: 'Sessions Court, Saket',
  },
  {
    title: 'Anjali Mehta vs. State',
    sub: 'CRL.A 812/2025 · 14:00',
    chip: 'ARGUMENTS',
    court: 'Delhi High Court',
  },
];

/** Liberty rows use the ledger's real headline strings (liberty_ledger_service
 *  `_HEADLINES` / `_computed_default_bail_headline`), hedges intact. */
export const CRIMINAL_LIBERTY: LibertyRow[] = [
  {
    kind: 'exposed',
    days: '18',
    headline: 'Default bail may accrue as early as 21 Nov — verify chargesheet status',
    caseLine: 'State vs. Rohit Kumar · in custody · day 42 today',
  },
  {
    kind: 'needs_answer',
    headline: 'Chargesheet filed? If not, default-bail clock runs',
    caseLine: 'State vs. Imran Shaikh',
  },
];

export const CRIMINAL_DEADLINES: DeadlineRow[] = [
  { days: '2', what: 'File reply', matter: 'Ravi v. State', tag: 'FILING' },
  { days: '12', what: 'Appeal limitation expires', matter: 'Singh appeal', tag: 'LIMITATION' },
];

export const CRIMINAL_NOT_ASSESSED =
  '3 matters not yet assessed — add custody facts on the matter page to include them.';

export const CRIMINAL_COURT_UPDATES: CourtUpdateRow[] = [
  {
    lead: 'Listed tomorrow',
    summary: 'Business entered for 3 Nov 2026 · posted to 4 Nov 2026 for ARGUMENTS',
    meta: 'Anjali Mehta vs. State · checked 3 Nov, 5:00 am',
  },
  {
    summary:
      'Business entered for 28 Oct 2026 · posted to 12 Nov 2026 for EVIDENCE · "ADJOURNED" · Status PENDING',
    meta: 'State vs. Imran Shaikh · checked 3 Nov, 5:00 am',
  },
];
